import { useSyncExternalStore } from "react";
import { companyService } from "../services/api";
import { PRODUCT_THEME_CHANGED_EVENT } from "../design-system/themes";
import payceeyLogo from "../assets/payceey-logo.png";
import { buildManifest, fitIcon, FALLBACK_APP_NAME, ICON_SIZES } from "./manifest";

/** Survives logout (unlike auth `companyData`) so the installed app keeps its identity. */
const IDENTITY_KEY = "pwaIdentity";
export const IOS_INSTALL_EVENT = "pwa-ios-install";

const PWA_ENABLED = process.env.REACT_APP_PWA_ENABLED !== "false";

let head = { name: FALLBACK_APP_NAME, icons: [] };
let applyToken = 0;

export const getAppIdentity = () => head;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (!src.startsWith("data:")) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function renderIcons(src) {
  const img = await loadImage(src);
  return ICON_SIZES.map((size) => {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, size, size);
    const { x, y, width, height } = fitIcon(img.naturalWidth, img.naturalHeight, size);
    ctx.drawImage(img, x, y, width, height);
    return { src: canvas.toDataURL("image/png"), size };
  });
}

function currentThemeColor() {
  const channels = getComputedStyle(document.documentElement)
    .getPropertyValue("--ds-primary")
    .trim();
  const ctx = document.createElement("canvas").getContext("2d");
  ctx.fillStyle = "#165b5d";
  if (channels) ctx.fillStyle = `hsl(${channels})`;
  return ctx.fillStyle;
}

function upsert(selector, tag, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function writeHead() {
  const themeColor = currentThemeColor();
  upsert('meta[name="theme-color"]', "meta", { name: "theme-color", content: themeColor });
  upsert('meta[name="apple-mobile-web-app-title"]', "meta", {
    name: "apple-mobile-web-app-title",
    content: head.name,
  });
  if (!head.icons.length) return;
  const manifest = buildManifest({
    name: head.name,
    origin: window.location.origin,
    themeColor,
    icons: head.icons,
  });
  upsert('link[rel="manifest"]', "link", {
    rel: "manifest",
    href: `data:application/manifest+json,${encodeURIComponent(JSON.stringify(manifest))}`,
  });
  upsert('link[rel="apple-touch-icon"]', "link", {
    rel: "apple-touch-icon",
    href: head.icons[0].src,
  });
}

async function applyIdentity({ name, logo }) {
  const token = ++applyToken;
  let icons;
  try {
    icons = await renderIcons(logo || payceeyLogo);
  } catch {
    icons = await renderIcons(payceeyLogo).catch(() => []);
  }
  if (token !== applyToken) return;
  head = { name: (name || "").trim() || FALLBACK_APP_NAME, icons };
  writeHead();
}

function readCachedIdentity() {
  try {
    return JSON.parse(localStorage.getItem(IDENTITY_KEY) || "null");
  } catch {
    return null;
  }
}

function initIdentity() {
  const cached = readCachedIdentity();
  applyIdentity(cached || { name: FALLBACK_APP_NAME, logo: null });

  companyService
    .getCompanyByDomain()
    .then((res) => {
      const company = res?.data?.company;
      const next = company ? { name: company.name || "", logo: company.logo || null } : null;
      if (next && cached && next.name === cached.name && next.logo === cached.logo) return;
      try {
        if (next) localStorage.setItem(IDENTITY_KEY, JSON.stringify(next));
        else localStorage.removeItem(IDENTITY_KEY);
      } catch {
        /* storage full: identity still applied for this session */
      }
      applyIdentity(next || { name: FALLBACK_APP_NAME, logo: null });
    })
    .catch(() => {
      /* offline / server down: keep cached identity */
    });

  window.addEventListener(PRODUCT_THEME_CHANGED_EVENT, writeHead);
}

/* ---------- Install prompt ---------- */

let deferredPrompt = null;
const installListeners = new Set();
const emitInstall = () => installListeners.forEach((l) => l());
const subscribeInstall = (l) => {
  installListeners.add(l);
  return () => installListeners.delete(l);
};

export function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function isIOS() {
  const ua = window.navigator.userAgent || "";
  return (
    /iphone|ipad|ipod/i.test(ua) ||
    (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1)
  );
}

/** True when the "Install app" entry should be offered on this device. */
export function useCanInstallApp() {
  const hasPrompt = useSyncExternalStore(subscribeInstall, () => deferredPrompt !== null);
  return PWA_ENABLED && !isStandalone() && (hasPrompt || isIOS());
}

export async function promptInstallApp() {
  if (deferredPrompt) {
    const evt = deferredPrompt;
    deferredPrompt = null;
    emitInstall();
    evt.prompt();
    await evt.userChoice.catch(() => null);
    return;
  }
  if (isIOS()) window.dispatchEvent(new Event(IOS_INSTALL_EVENT));
}

function initInstallPrompt() {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    emitInstall();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    emitInstall();
  });
}

/* ---------- Service worker ---------- */

function initServiceWorker() {
  if (process.env.NODE_ENV !== "production" || !("serviceWorker" in navigator)) return;
  if (!PWA_ENABLED) {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => regs.forEach((r) => r.unregister()))
      .catch(() => {});
    return;
  }
  const register = () =>
    navigator.serviceWorker
      .register(`${process.env.PUBLIC_URL}/service-worker.js`)
      .catch((err) => console.warn("[PWA] service worker registration failed:", err));
  if (document.readyState === "complete") register();
  else window.addEventListener("load", register, { once: true });
}

export function initPwa() {
  if (typeof window === "undefined") return;
  initInstallPrompt();
  initIdentity();
  initServiceWorker();
}
